import React from 'react';
import { Content as TabsContent } from '@radix-ui/react-tabs';
import { TabPanelsTreeNodeDataProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { TabGroupContext } from './TabGroup';
import { backgroundInlineStyles, BACKGROUND_SRC_DEFAULT_CLASSES } from './backgroundInlineStyles';
import { cn } from '../../utils/cn';

type TabPanelsProps = {
  id: string;
  editorClassName?: string;
  children: React.ReactNode;
  isEditor?: boolean;
  staticAssetsBaseURL?: string | null;
} & TabPanelsTreeNodeDataProps;

export const TabPanels = React.forwardRef<HTMLDivElement, TabPanelsProps>(
  (
    {
      id,
      className,
      editorClassName,
      children,
      isEditor = false,
      backgroundSrc,
      staticAssetsBaseURL,
      // `type` is a tree-node schema discriminator, not a DOM attribute.
      type: _type,
      ...props
    },
    ref,
  ) => {
    const ctx = React.useContext(TabGroupContext);
    const activeIndex = ctx?.activeIndex ?? 0;
    const panelIds = ctx?.panelIds ?? [];
    const childArray = React.Children.toArray(children);

    return (
      <div
        ref={ref}
        id={id}
        className={cn(backgroundSrc ? BACKGROUND_SRC_DEFAULT_CLASSES : undefined, className, editorClassName)}
        style={backgroundInlineStyles(backgroundSrc, staticAssetsBaseURL)}
        {...props}
      >
        {childArray.map((child, index) => {
          const hidden = index !== activeIndex;
          const key = panelIds[index] ?? index;
          return isEditor ? (
            <TabsContent key={key} value={`tab-${index}`} forceMount hidden={hidden}>
              {child}
            </TabsContent>
          ) : (
            <TabsContent key={key} value={`tab-${index}`} forceMount asChild hidden={hidden}>
              {child}
            </TabsContent>
          );
        })}
      </div>
    );
  },
);

TabPanels.displayName = 'TabPanels';
